import React, { useState } from "react"
import { CHANGELOG_DATA } from "./changelogData"
import { CH_CATEGORIES, CH_INTERNAL_CATEGORIES, CHANGELOG_GITHUB_ISSUES_BASE_URL, ChangelogCategory, ChangelogInternalCategory, ChangelogItem } from "./changelog"
import clsx from "clsx"
import { useAuth } from "../../AppContext"
import { CONST_USER_ROLE_DG, CONST_USER_ROLE_IT, CONST_USER_ROLE_PRESIDENT, CONST_USER_ROLE_SUPER_ADMIN } from "@common-constants/*"
import "./custom.scss"

// Liens vers les tickets GitHub — réservés à ROLE_SUPER_ADMIN
const IssueLinks: React.FC<{ issues?: number[] }> = ({ issues }) => {
     if (!issues || issues.length === 0) return null
     return (
          <span className="d-inline-flex flex-wrap gap-2 ms-2 align-middle">
               {issues.map(n => (
                    <a
                         key={n}
                         href={`${CHANGELOG_GITHUB_ISSUES_BASE_URL}${n}`}
                         target="_blank"
                         rel="noopener noreferrer"
                         onClick={e => e.stopPropagation()}
                         className="badge badge-light-primary fw-bold fs-9 px-2 py-1 text-decoration-none"
                         title="Voir le ticket GitHub"
                    >
                         <i className="bi bi-github fs-8 me-1"></i>#{n}
                    </a>
               ))}
          </span>
     )
}

// Un groupe de la section interne (fonctionnel ou technique)
const InternalGroup: React.FC<{ label: string; icon: string; items?: ChangelogItem[]; isSuperAdmin: boolean }> = ({ label, icon, items, isSuperAdmin }) => {
     if (!items || items.length === 0) return null
     return (
          <div className="mb-8">
               <div className="d-flex align-items-center mb-5">
                    <i className={`bi ${icon} fs-5 text-gray-600 me-3`}></i>
                    <h6 className="fw-boldest text-gray-700 mb-0 uppercase fs-8">{label}</h6>
               </div>
               <ul className="list-unstyled ps-2 ps-lg-12">
                    {items.map((item, i) => (
                         <li key={i} className="mb-6">
                              <div className="d-flex align-items-start mb-2">
                                   <i className="bi bi-gear-fill text-gray-500 fs-6 me-4 mt-1"></i>
                                   <span className="text-gray-700 fs-6 fw-medium lh-lg">
                                        {item.text}
                                        {isSuperAdmin && <IssueLinks issues={item.issues} />}
                                   </span>
                              </div>
                              {item.tags && item.tags.length > 0 && (
                                   <div className="d-flex flex-wrap gap-2 ps-9">
                                        {item.tags.map((tag, tagIndex) => (
                                             <span
                                                  key={tagIndex}
                                                  className="badge badge-light-secondary text-gray-600 fw-bold fs-9 px-3 py-1"
                                                  style={{ border: "1px solid #E1E3EA" }}
                                             >
                                                  {tag}
                                             </span>
                                        ))}
                                   </div>
                              )}
                         </li>
                    ))}
               </ul>
          </div>
     )
}

const ChangelogPage: React.FC = () => {
     const { currentUser } = useAuth()
     const roles = currentUser?.roles ?? []
     const isSuperAdmin = roles.includes(CONST_USER_ROLE_SUPER_ADMIN)
     const canSeeInternal =
          isSuperAdmin || roles.includes(CONST_USER_ROLE_IT) || roles.includes(CONST_USER_ROLE_PRESIDENT) || roles.includes(CONST_USER_ROLE_DG)

     const [selectedId, setSelectedId] = useState<number>(CHANGELOG_DATA[0].id)
     const selectedVersion = CHANGELOG_DATA.find(v => v.id === selectedId) || CHANGELOG_DATA[0]

     return (
          <div className={"d-flex justify-content-center"}>
               <div className={"w-100 w-lg-75 w-xxl-70"}>
                    <>
                         {/* NAVIGATION MOBILE : Horizontale pour éviter le scroll vertical infini */}
                         <div className="d-lg-none mb-8 px-2">
                              <div className="version-picker-mobile">
                                   {CHANGELOG_DATA.map(item => (
                                        <div
                                             key={item.id}
                                             onClick={() => setSelectedId(item.id)}
                                             className={clsx(
                                                  "flex-shrink-0 px-6 py-2 rounded-pill fw-boldest fs-8 border transition-all cursor-pointer",
                                                  selectedId === item.id ? "bg-primary text-white border-primary shadow-sm" : "bg-white text-gray-600 border-gray-300"
                                             )}
                                        >
                                             {item.version}
                                        </div>
                                   ))}
                              </div>
                         </div>

                         <div className="row g-6 g-lg-10">
                              {/* NAVIGATION DESKTOP : Timeline parfaitement alignée */}
                              <div className="col-lg-4 d-none d-lg-block">
                                   <div className="card border-0 shadow-sm sticky-top" style={{ top: "20px" }}>
                                        <div className="card-body p-0">
                                             <div className="p-6 border-bottom bg-light-secondary bg-opacity-30">
                                                  <h5 className="mb-0 fw-boldest uppercase fs-8 text-gray-700">Mises à jour ({CHANGELOG_DATA.length})</h5>
                                             </div>
                                             <div className="mh-500px scroll-y p-8">
                                                  <div className="timeline-v2">
                                                       {CHANGELOG_DATA.map(item => (
                                                            <div
                                                                 key={item.id}
                                                                 className={clsx("timeline-item cursor-pointer mb-10", selectedId === item.id && "active")}
                                                                 onClick={() => setSelectedId(item.id)}
                                                            >
                                                                 <div className="timeline-line"></div>
                                                                 <div className={clsx("timeline-icon", selectedId === item.id ? "bg-primary" : "bg-gray-300")}></div>
                                                                 <div className="timeline-content">
                                                                      <div className="d-flex flex-column">
                                                                           <span
                                                                                className={clsx("fw-boldest fs-6 mb-1", selectedId === item.id ? "text-primary" : "text-gray-800")}
                                                                           >
                                                                                {item.version}
                                                                           </span>
                                                                           <span className="text-muted fs-9 fw-bold uppercase">{item.date}</span>
                                                                           <span className="text-gray-500 fs-8 mt-2 line-clamp-1">{item.title}</span>
                                                                      </div>
                                                                 </div>
                                                            </div>
                                                       ))}
                                                  </div>
                                             </div>
                                        </div>
                                   </div>
                              </div>

                              {/* ZONE DE DÉTAILS */}
                              <div className="col-lg-8">
                                   <div className="card border-0 shadow-sm animate__animated animate__fadeInFast">
                                        <div className="card-body p-8 p-lg-12">
                                             <div className="mb-10">
                                                  <div className="d-flex align-items-center justify-content-between mb-5">
                                                       <span className="badge badge-light-primary fw-boldest fs-9 px-3 py-2 uppercase border border-primary border-opacity-10">
                                                            {selectedId === CHANGELOG_DATA[0].id ? "Dernière version" : "Version antérieure"}
                                                       </span>
                                                       <span className="text-muted fw-bold fs-8 opacity-75">
                                                            <i className="bi bi-calendar3 me-2 text-primary"></i>
                                                            {selectedVersion.date}
                                                       </span>
                                                  </div>
                                                  <h2 className="fw-boldest text-gray-900 mb-2 fs-1">{selectedVersion.title}</h2>
                                                  <div className="fs-4 text-primary fw-bold opacity-75">{selectedVersion.version}</div>
                                             </div>

                                             <div className="separator separator-dashed my-8"></div>

                                             {(Object.keys(CH_CATEGORIES) as ChangelogCategory[]).map(catKey => {
                                                  const items = selectedVersion.changes[catKey]
                                                  if (!items || items.length === 0) return null

                                                  const cat = CH_CATEGORIES[catKey]
                                                  return (
                                                       <div key={catKey} className="mb-10">
                                                            <div className="d-flex align-items-center mb-6">
                                                                 <div className="symbol symbol-40px me-4">
                                                                      <div className={`symbol-label bg-light-${cat.color}`}>
                                                                           <i className={`bi ${cat.icon} fs-4 text-${cat.color}`}></i>
                                                                      </div>
                                                                 </div>
                                                                 <h5 className="fw-boldest text-gray-800 mb-0 uppercase fs-7">{cat.label}</h5>
                                                            </div>
                                                            <ul className="list-unstyled ps-2 ps-lg-12">
                                                                 {items.map((item, i) => (
                                                                      <li key={i} className="mb-6">
                                                                           {" "}
                                                                           {/* Augmenté margin-bottom pour l'espace des tags */}
                                                                           <div className="d-flex align-items-start mb-2">
                                                                                <i className={`bi bi-check2-circle text-${cat.color} fs-5 me-4 mt-1`}></i>
                                                                                <span className="text-gray-700 fs-6 fw-medium lh-lg">
                                                                                     {item.text}
                                                                                     {isSuperAdmin && <IssueLinks issues={item.issues} />}
                                                                                </span>
                                                                           </div>
                                                                           {/* Affichage des tags si présents */}
                                                                           {item.tags && item.tags.length > 0 && (
                                                                                <div className="d-flex flex-wrap gap-2 ps-9">
                                                                                     {item.tags.map((tag, tagIndex) => (
                                                                                          <span
                                                                                               key={tagIndex}
                                                                                               className="badge badge-light-secondary text-gray-600 fw-bold fs-9 px-3 py-1"
                                                                                               style={{ border: "1px solid #E1E3EA" }}
                                                                                          >
                                                                                               {tag}
                                                                                          </span>
                                                                                     ))}
                                                                                </div>
                                                                           )}
                                                                      </li>
                                                                 ))}
                                                            </ul>
                                                       </div>
                                                  )
                                             })}

                                             {/* Section interne — visible uniquement par les rôles habilités, scindée en 2 sous-catégories */}
                                             {canSeeInternal &&
                                                  selectedVersion.internal &&
                                                  ((selectedVersion.internal.functional?.length ?? 0) + (selectedVersion.internal.technical?.length ?? 0)) > 0 && (
                                                       <div className="mt-4">
                                                            <div className="separator separator-dashed my-8"></div>
                                                            <div className="d-flex align-items-center mb-8">
                                                                 <div className="symbol symbol-40px me-4">
                                                                      <div className="symbol-label bg-light-dark">
                                                                           <i className="bi bi-shield-lock fs-4 text-gray-700"></i>
                                                                      </div>
                                                                 </div>
                                                                 <div className="d-flex flex-column">
                                                                      <h5 className="fw-boldest text-gray-800 mb-0 uppercase fs-7">Technique & interne</h5>
                                                                      <span className="text-muted fs-9 fw-bold">Réservé — non visible par les utilisateurs standards</span>
                                                                 </div>
                                                            </div>
                                                            {(Object.keys(CH_INTERNAL_CATEGORIES) as ChangelogInternalCategory[]).map(catKey => (
                                                                 <InternalGroup
                                                                      key={catKey}
                                                                      label={CH_INTERNAL_CATEGORIES[catKey].label}
                                                                      icon={CH_INTERNAL_CATEGORIES[catKey].icon}
                                                                      items={selectedVersion.internal?.[catKey]}
                                                                      isSuperAdmin={isSuperAdmin}
                                                                 />
                                                            ))}
                                                       </div>
                                                  )}
                                        </div>
                                   </div>
                              </div>
                         </div>
                    </>
               </div>
          </div>
     )
}

export default ChangelogPage
